/**
 * brandColor.js — keep a tenant's brand colour legible on our dark UI.
 *
 * Institutions pick their own primary colour. Some are dark (deep navy, maroon,
 * charcoal) and, used as text directly on our dark-grey surfaces (#1A1A1A /
 * #242424), they all but disappear. `readableOn` returns the SAME hue lifted to
 * the lightness needed to clear a WCAG contrast threshold against that surface —
 * so the text stays on-brand but readable. Light/medium brands already pass and
 * are returned unchanged.
 */

export const hexToRgb = (hex) => {
  // Accept 3- or 6-digit hex (the backend allows both). Expand shorthand
  // (#abc → #aabbcc) before parsing — parseInt("abc",16) is NOT #aabbcc — and
  // fall back to brand green for malformed input so contrast math never yields NaN.
  let h = String(hex || "").trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) h = "4caf50";
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

// Relative luminance per WCAG 2.x.
const channel = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
export const luminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

// Contrast ratio (1–21) between two hex colours.
export const contrastRatio = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
};

export const hexToHsl = (hex) => {
  let { r, g, b } = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h, s, l };
};

export const hslToHex = (h, s, l) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
};

/**
 * Lift `brand` (same hue) until it clears `minContrast` against `bg`. Returns
 * `brand` untouched when it already passes. `minContrast` defaults to 4.5 (WCAG
 * AA for normal text); pass ~3 for large text / non-text UI accents.
 *
 * @param {string} brand        institution primary colour (hex)
 * @param {string} [bg]         surface the text sits on (default our paper #242424)
 * @param {number} [minContrast] required contrast ratio
 */
export const readableOn = (brand, bg = "#242424", minContrast = 4.5) => {
  try {
    if (!brand || contrastRatio(brand, bg) >= minContrast) return brand;
    const { h, s } = hexToHsl(brand);
    // Keep it colourful (don't wash a navy out to near-white) but bright enough.
    // A near-grey brand stays grey — don't fabricate a hue by flooring saturation.
    const sat = s < 0.12 ? s : Math.max(0.4, Math.min(0.92, s));
    for (let l = 0.45; l <= 0.97; l += 0.03) {
      const cand = hslToHex(h, sat, l);
      if (contrastRatio(cand, bg) >= minContrast) return cand;
    }
    return hslToHex(h, sat, 0.96);
  } catch {
    return brand;
  }
};
